import { useState } from "react";
import { mismatchedProductionEquipment, replaceProductionEquipment, type Scenario } from "@h2lca/core";
import { Button, SelectField, TextArea, TextField } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { technologiesFor, useMaster } from "@/store/master";
import { useWorkshop } from "@/store/workshop";
import { GlossaryTip } from "../GlossaryTip";
import { NumField } from "../NumField";
import { Section } from "../Section";

/** ① 산정목적,기본조건 , 목적 및 범위 정의의 첫 절반 */
export function BasisStep({ scenario }: { scenario: Scenario }) {
  const data = useMaster((s) => s.data);
  const update = useWorkshop((s) => s.update);
  const techs = technologiesFor(data, "production");
  const tech = data.technologies.find((t) => t.id === scenario.basis.productionTechId);
  const [replacementBackup, setReplacementBackup] = useState<Scenario | null>(null);
  const [pendingTech, setPendingTech] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [changeError, setChangeError] = useState<string | null>(null);
  const mismatched = mismatchedProductionEquipment(scenario);
  const targetTech = pendingTech ?? scenario.basis.productionTechId;

  function keepEquipment() {
    update((s) => {
      const next = { ...s, exampleId: undefined, basis: { ...s.basis, productionTechId: targetTech } };
      return { ...next, basis: { ...next.basis, technologyReview: { productionTechId: targetTech, equipmentIds: mismatchedProductionEquipment(next).map((e) => e.id), note: reviewNote.trim() } } };
    });
    setPendingTech(null);
  }

  function replaceEquipment() {
    try {
      const next = replaceProductionEquipment(scenario, targetTech, data.templates, data.methods.find((m) => m.id === scenario.method)!);
      // 사용자가 선택한 동작 안에서 교체 전 입력 전체를 복구 가능한 파일로 보관한다.
      useWorkshop.getState().exportFile();
      setReplacementBackup(structuredClone(scenario));
      update(() => next);
      setPendingTech(null);
      setReviewNote("");
      setChangeError(null);
    } catch (error) { setChangeError(error instanceof Error ? error.message : "생산 설비를 교체하지 못했습니다"); }
  }

  return (
    <Section
      title={
        <>
          산정목적과 기본조건 <GlossaryTip k="goal_scope" />
        </>
      }
      desc="왜 산정하는지와 무엇을 기준으로 산정하는지를 먼저 적습니다. 여기서 정한 기준 수소량이 모든 설비 입력값의 기준이 됩니다."
    >
      <div className="ws-form-grid">
        <TextField label="시나리오 이름" value={scenario.name} onChange={(e) => update((s) => ({ ...s, name: e.target.value }))} />
        <NumField label="기준연도" unit="년" value={scenario.meta.baseYear} allowNull={false} min={2000} step={1} onChange={(v) => v !== null && update((s) => ({ ...s, meta: { ...s.meta, baseYear: Math.round(v) } }))} hint="시나리오 운전자료의 기준연도. 배출계수의 원본 대표연도는 별도로 기록됩니다." />
      </div>
      <TextArea label="산정목적" rows={3} value={scenario.meta.purpose} onChange={(e) => update((s) => ({ ...s, meta: { ...s.meta, purpose: e.target.value } }))} placeholder="예: 계통전력 수전해 수소의 충전소 인도 기준 온실가스 배출량 산정(교육용)" hint="의도된 용도,이유,대상 청중을 한두 문장으로. 보고서 첫 장에 그대로 실립니다." />

      <div className="ws-form-grid">
        <SelectField label="생산방식(대상기술)" value={pendingTech ?? scenario.basis.productionTechId} onChange={(e) => { setPendingTech(e.target.value === scenario.basis.productionTechId ? null : e.target.value); setReviewNote(""); setChangeError(null); }} hint={pendingTech ? "아래에서 설비 처리 방식을 선택하면 변경됩니다." : tech?.description}>
          {techs.map((t) => (
            <option key={t.id} value={t.id} disabled={t.status === "placeholder"}>
              {t.name}
              {t.status === "placeholder" ? " (협의 후 제공)" : ""}
            </option>
          ))}
        </SelectField>
        <NumField
          label={
            <span className="inline-flex items-center gap-label">
              산정 기준 수소량
              <InfoTip label="산정 기준 수소량 설명">
                모든 설비의 입력값(전력, 연료, 용수 사용량)은 <b>이 양의 수소를 만드는 동안</b>의 사용량입니다. 팔레트에서 설비를 추가하면 기본값이 이 양에 비례해 채워집니다. 기준량을 나중에 바꿔도 이미 놓인 설비의 값은 자동으로 바뀌지 않으므로, 운전자료 입력 단계의 ‘기본값으로 되돌리기’로 다시 채우세요.
              </InfoTip>
            </span>
          }
          unit="kg"
          value={scenario.basis.batchH2Kg}
          allowNull={false}
          min={0}
          onChange={(v) => v !== null && v > 0 && update((s) => ({ ...s, basis: { ...s.basis, batchH2Kg: v } }))}
          hint="예: 100 kg. 설비 한 회분 또는 대표 운전 구간의 생산량"
        />
      </div>

      {replacementBackup && <div className="note note-info flex flex-col gap-content" role="status">
        <p className="text-footnote">생산 설비를 교체했습니다. 이 단계를 떠나기 전에는 교체 전 입력 전체로 되돌릴 수 있습니다. 이후에는 내려받은 JSON 백업을 사용하세요.</p>
        <div><Button onClick={() => { update(() => replacementBackup); setReplacementBackup(null); setPendingTech(null); setReviewNote(""); }}>방금 교체 취소</Button></div>
      </div>}

      {(pendingTech || mismatched.length > 0) && (
        <div className="note note-warn flex flex-col gap-content" role="region" aria-label="생산방식 변경 검토">
          <p className="text-caption font-semibold">생산방식과 설비 구성을 함께 확인해 주세요</p>
          <p className="text-footnote">{data.technologies.find((t) => t.id === targetTech)?.name ?? targetTech}에 맞춰 생산 설비를 교체하거나, 기존 설비를 유지하는 근거를 기록하세요. 교체하면 기존 입력 전체를 JSON 파일로 내려받고 생산 전용 설비를 기본값으로 바꿉니다. 저장·이송 및 사용자 정의 설비의 활동자료는 유지하고 수소 흐름은 다시 연결합니다. 백업은 ‘가져오기’로 복구할 수 있습니다.</p>
          <TextArea label="기존 설비 유지 근거" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} hint="복합 공정, 대표기술 선정 이유 등 실제 구성의 적합성을 적어 주세요. 유지 시 검토 경고가 결과에 남습니다." />
          <div className="flex flex-wrap gap-item">
            <Button onClick={keepEquipment} disabled={!reviewNote.trim()}>근거를 기록하고 기존 설비 유지</Button>
            <Button onClick={replaceEquipment}>기존 입력 백업 후 생산 설비 교체</Button>
            {pendingTech && <Button onClick={() => setPendingTech(null)}>변경 취소</Button>}
          </div>
          {scenario.basis.technologyReview && <p className="text-footnote">기록된 유지 근거: {scenario.basis.technologyReview.note}</p>}
          {changeError && <p role="alert" className="text-tier-tight text-footnote">{changeError}</p>}
        </div>
      )}

      <div className="ws-form-grid">
        <NumField label="연간 생산량 (선택)" unit="kg/년" value={scenario.basis.annualOutputKg ?? null} min={0} onChange={(v) => update((s) => ({ ...s, basis: { ...s.basis, annualOutputKg: v ?? undefined } }))} hint="참고 정보. 계산에는 쓰지 않습니다" />
        <NumField label="연간 운전시간 (선택)" unit="h/년" value={scenario.basis.operatingHoursPerYear ?? null} min={0} onChange={(v) => update((s) => ({ ...s, basis: { ...s.basis, operatingHoursPerYear: v ?? undefined } }))} hint="참고 정보. 계산에는 쓰지 않습니다" />
      </div>

      <p className="text-footnote text-ink-subtle">
        본 프로그램은 기후변화 영향범주(온실가스)만 다룹니다. 산성화, 부영양화 등 다른 영향범주는 산정대상이 아닙니다. <GlossaryTip k="lcia" />
      </p>
    </Section>
  );
}
